import React, { forwardRef, useEffect, useState } from 'react'
import styled from '../themes/styled'
import Text from 'particles/Text'
import Icon, { IconName } from 'particles/Icon'
import Tooltip from 'particles/Tooltip'

interface LabelContainerProps {
    isCommentable?: boolean
    isCommented?: boolean
    isActive?: boolean
    width?: string
}

export type LabelSize = 's' | 'm' | 'l' | 'xl' | 'xxl'

export type LinkConfig = {
    label: string
    href: string
}

export const titleSize = {
    s: 'bodyS500',
    m: 'bodyM500',
    l: 'bodyM700',
    xl: 'H3',
    xxl: 'H4',
} as const
interface LabelProps {
    title: string
    subtext?: string
    size?: LabelSize
    width?: string
    isCommentable?: boolean
    isCommented?: boolean
    isRequired?: boolean
    icon?: IconName
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
    onCommentClick?: (e: React.MouseEvent<HTMLDivElement>) => void
    className?: string
    isActivated?: boolean
    id?: string
    tooltip?: string
    link?: LinkConfig
}

const LabelContainer = styled.label<LabelContainerProps>(({ theme, width, isCommentable, isCommented, isActive }) => ({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 80,
    width: width,
    gap: theme.spacing[100],
    '& .title-area': {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing[100],
        cursor: isCommentable ? 'pointer' : 'default',
        backgroundColor: isActive
            ? theme.colors.surface['warningActive']
            : isCommented && isCommentable
              ? theme.colors.surface['warningPastel']
              : 'transparent',
        borderBottom: isCommentable
            ? `1.5px solid ${isCommented || isActive ? theme.colors.border.warning : 'transparent'}`
            : 'none',
        '.label': {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing[100],
        },
        '.title': {
            maxWidth: '100%',
            wordBreak: 'break-word',
        },
        '&:hover': {
            backgroundColor:
                isActive && isCommentable
                    ? theme.colors.surface.warningActive
                    : isCommentable
                      ? theme.colors.surface.neutralActionSubPastelHover
                      : 'transparent',
            borderBottom: isCommentable
                ? `1.5px solid ${theme.colors.border[isActive ? 'warning' : 'tertiary']}`
                : 'none',
        },

        '& .icon': {
            opacity: isActive || isCommented ? 1 : 0,
            flexShrink: 0,
        },
        '&:hover .icon, &:active .icon': {
            opacity: 1,
        },
    },
    '.link': {
        textDecoration: 'none',
        cursor: 'pointer',
        div: {
            cursor: 'pointer',
        },
    },
    '&:hover .link': {
        cursor: 'pointer',
        div: {
            cursor: 'pointer',
        },
    },
}))

const Label = forwardRef<HTMLLabelElement, LabelProps>(
    (
        {
            title,
            subtext,
            size = 'm',
            width = '100%',
            isRequired = true,
            isCommentable = false,
            isCommented = false,
            icon = 'commentPlus',
            onClick,
            onCommentClick,
            className,
            isActivated = false,
            id,
            tooltip,
            link,
        },
        ref,
    ) => {
        const [isActive, setIsActive] = useState(false)

        const renderCommentIcon =
            isCommentable && !!icon ? <Icon name={icon} size={size === 's' ? '16px' : '20px'} className="icon" /> : null
        const renderSubtext = subtext && (
            <Text scale="bodyS400" color="secondary">
                {subtext}
            </Text>
        )

        const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
            e.preventDefault()
            e.stopPropagation()
            if (isCommentable && onCommentClick) {
                setIsActive(!isActive)
                onCommentClick(e)
            }
            if (onClick) {
                onClick(e)
            }
        }
        useEffect(() => {
            setIsActive(isActivated)
        }, [isActivated])

        return (
            <LabelContainer
                className={className}
                isCommentable={isCommentable}
                isCommented={isCommented}
                isActive={isActive}
                width={width}
                ref={ref}
                id={id}
            >
                <div className="title-area" onClick={handleClick}>
                    <div className="label">
                        <Text scale={titleSize[size]} color="primary" className="title">
                            {title} {isRequired && '*'}
                        </Text>
                        {tooltip && (
                            <Tooltip label={tooltip} placement="bottom">
                                <div className="label">
                                    <Icon name="info" size={size === 's' ? '16px' : '20px'} />
                                </div>
                            </Tooltip>
                        )}
                    </div>
                    {renderCommentIcon}
                </div>
                {renderSubtext}
                {link?.href && (
                    <a href={link.href} className="link">
                        <Text scale="bodyS400" color="action">
                            {link?.label || link.href}
                        </Text>
                    </a>
                )}
            </LabelContainer>
        )
    },
)

Label.displayName = 'Label'

export default Label
